import '@/shared/tracking/events.quiz-share-guest';
import '@/shared/tracking/events.quiz-trial-gate-copy';

import { trackEvent } from '@zep/logger/tracking';

import { toTrackingFailReason } from '@/shared/features/quiz-folder/utils/folderTracking';
import { type QuizCopyContext } from '@/shared/tracking/quizCopySource';

/*
 * 복사 퍼널 네 칸(`auth_start` → `resume_view` → `copy_complete`, 곁가지 `block_view`)의
 * 출처별(`guest_share`/`trial_gate`) 분기를 이 파일 하나가 갖는다. 호출부는 출처를 모른다.
 */

export type QuizCopyResult =
  | { status: 'success'; copiedQuizsetId: string }
  | { status: 'fail'; error?: unknown };

/**
 * 실패 사유는 서버 코드만 싣는다 — 메시지에는 사용자 입력이 섞일 수 있다.
 * (에러 이름은 항상 `ZepQuizApiError` 라 원인을 가르지 못한다.)
 */
const toReason = toTrackingFailReason;

/**
 * 게스트가 인증 탭을 여는 순간. 로그인 사용자는 이 칸을 건너뛴다 — copy_click 대비 비율이 곧 비로그인 비중이다.
 *
 * 이미 열린 인증 탭을 앞으로 가져올 때는 부르지 않는다(새 시도가 아니다).
 */
export const trackQuizCopyAuthStart = (
  context: QuizCopyContext,
  sourceQuizsetId: string,
) => {
  if (context.copySource === 'guest_share' && context.shareFlowId) {
    trackEvent('quiz_share_guest_auth_start', {
      share_flow_id: context.shareFlowId,
      quiz_set_id: sourceQuizsetId,
    });
  }
  if (context.copySource === 'trial_gate' && context.trialSessionId) {
    trackEvent('quiz_play_trial_gate_auth_start', {
      trial_session_id: context.trialSessionId,
      quiz_set_id: sourceQuizsetId,
    });
  }
};

/**
 * 복사 전환 퍼널의 마지막 칸. 복사가 일어나는 두 자리(제자리 복사, 착지 경로)가 함께 쓴다.
 *
 * 이벤트 이름과 prop 은 바꾸지 않는다 — 클릭→완료 전환율의 시계열이 끊긴다.
 *
 * 출처(`copySource`)가 없으면 아무것도 보내지 않는다.
 */
export const trackQuizCopyComplete = (
  context: QuizCopyContext,
  sourceQuizsetId: string,
  result: QuizCopyResult,
) => {
  if (context.copySource === 'guest_share' && context.shareFlowId) {
    trackEvent('quiz_share_guest_copy_complete', {
      share_flow_id: context.shareFlowId,
      origin_quiz_set_id: sourceQuizsetId,
      ...(result.status === 'success'
        ? { result: 'success', copied_quiz_set_id: result.copiedQuizsetId }
        : { result: 'fail', reason: toReason(result.error) }),
    });
  }

  if (context.copySource === 'trial_gate' && context.trialSessionId) {
    trackEvent('quiz_play_trial_gate_copy_complete', {
      trial_session_id: context.trialSessionId,
      quiz_set_id: sourceQuizsetId,
      ...(result.status === 'success'
        ? { result: 'success', copied_quiz_set_id: result.copiedQuizsetId }
        : { result: 'fail', reason: toReason(result.error) }),
    });
  }
};

/**
 * 인증을 마치고 복사가 이어지는 시점.
 *
 * 착지 경로에서만 부른다. 이 이벤트 대비 `copy_complete` 비율이 인증 후 이탈률이다.
 */
export const trackQuizCopyResume = (
  context: QuizCopyContext,
  sourceQuizsetId: string,
) => {
  if (context.copySource === 'guest_share' && context.shareFlowId) {
    trackEvent('quiz_share_guest_resume_view', {
      share_flow_id: context.shareFlowId,
      quiz_set_id: sourceQuizsetId,
    });
  }
  if (context.copySource === 'trial_gate' && context.trialSessionId) {
    trackEvent('quiz_play_trial_gate_resume_view', {
      trial_session_id: context.trialSessionId,
      quiz_set_id: sourceQuizsetId,
    });
  }
};

/**
 * 학생 계정이라 복사가 막혔다.
 *
 * 차단을 **판정한 쪽**(`useCopyFlow`)이 한 번 부른다. 안내 화면(`CopyNoticeScreen`)은 문구만
 * 그리므로 계측하지 않는다.
 */
export const trackQuizCopyBlocked = (
  context: QuizCopyContext,
  sourceQuizsetId: string,
) => {
  if (context.copySource !== 'guest_share' || !context.shareFlowId) return;
  trackEvent('quiz_share_guest_block_view', {
    share_flow_id: context.shareFlowId,
    quiz_set_id: sourceQuizsetId,
  });
};
